import {createContext, type PropsWithChildren, useCallback, useContext, useEffect, useReducer, useRef, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import type {DungeonFormInput, DungeonFormOutput} from "@/dungeons/admin/form/DungeonFormSchema.ts";
import type {LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {
    dungeonEditorHistoryReducer,
    initialDungeonEditorHistoryState,
    type DungeonEditorHistoryEntry,
} from "./DungeonEditorHistoryState.ts";

type DungeonEditorHistory = {
    revision: number;
    canUndo: boolean;
    canRedo: boolean;
    record: (entry: DungeonEditorHistoryEntry) => void;
    undo: () => void;
    redo: () => void;
    clear: () => void;
};

const DungeonEditorHistoryContext = createContext<DungeonEditorHistory>({
    revision: 0,
    canUndo: false,
    canRedo: false,
    record: () => undefined,
    undo: () => undefined,
    redo: () => undefined,
    clear: () => undefined,
});

const isTextInput = (target: EventTarget | null) => target instanceof HTMLElement
    && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

export function DungeonEditorHistoryProvider({children}: PropsWithChildren) {
    const form = useFormContext<DungeonFormInput, unknown, DungeonFormOutput>();
    const levels = useWatch({control: form.control, name: "levels"});
    const [state, dispatch] = useReducer(dungeonEditorHistoryReducer, initialDungeonEditorHistoryState);
    const [revision, setRevision] = useState(0);
    const levelIdsRef = useRef<string[] | undefined>(undefined);

    const applyDocument = useCallback((levelId: string, document: LevelGeometryDocument) => {
        const index = (form.getValues("levels") ?? []).findIndex(level => level.id === levelId);
        if (index < 0) return false;
        form.setValue(`levels.${index}.geometry`, document, {shouldDirty: true, shouldValidate: true});
        return true;
    }, [form]);
    const record = useCallback((entry: DungeonEditorHistoryEntry) => {
        if (entry.before === entry.after) return;
        dispatch({type: "RECORD", entry});
    }, []);
    const undo = useCallback(() => {
        const entry = state.undoStack[state.undoStack.length - 1];
        if (entry && applyDocument(entry.levelId, entry.before)) {
            dispatch({type: "UNDO"});
            setRevision(value => value + 1);
        }
    }, [applyDocument, state.undoStack]);
    const redo = useCallback(() => {
        const entry = state.redoStack[state.redoStack.length - 1];
        if (entry && applyDocument(entry.levelId, entry.after)) {
            dispatch({type: "REDO"});
            setRevision(value => value + 1);
        }
    }, [applyDocument, state.redoStack]);
    const clear = useCallback(() => dispatch({type: "CLEAR"}), []);

    useEffect(() => {
        const levelIds = (levels ?? []).map(level => level.id);
        const previous = levelIdsRef.current;
        levelIdsRef.current = levelIds;
        if (previous && (previous.length !== levelIds.length
            || previous.some((levelId, index) => levelId !== levelIds[index]))) clear();
    }, [clear, levels]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isTextInput(event.target) || !(event.ctrlKey || event.metaKey)) return;
            if (event.key.toLowerCase() === "z") {
                const action = event.shiftKey ? redo : undo;
                const available = event.shiftKey ? state.redoStack.length > 0 : state.undoStack.length > 0;
                if (available) {
                    event.preventDefault();
                    action();
                }
            } else if (event.key.toLowerCase() === "y" && state.redoStack.length > 0) {
                event.preventDefault();
                redo();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [redo, state.redoStack.length, state.undoStack.length, undo]);

    return <DungeonEditorHistoryContext.Provider value={{
        revision,
        canUndo: state.undoStack.length > 0,
        canRedo: state.redoStack.length > 0,
        record,
        undo,
        redo,
        clear,
    }}>{children}</DungeonEditorHistoryContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useDungeonEditorHistory = () => useContext(DungeonEditorHistoryContext);
